import React from 'react';
import { Classes } from '@blueprintjs/core';
import { RBACRunbookPermission } from '@kentik/ui-shared/rbac/constants';
import { InfobloxLogo } from 'app/components/InfobloxLogo';

// RBAC read permissions required to access a tab. Tabs not listed here are accessible to all full AI users.
const TAB_READ_PERMISSIONS = {
  runbooks: [RBACRunbookPermission.READ]
};

export const KENTIK_AI_SETTINGS_BASE_URL = '/v4/settings/kentik-ai';

const INFOBLOX_IQ_TAB_ID = 'infoblox-iq';

/**
 * Built-in integrations need Kentik AI only: every user, not just admins, connects their own account there.
 */
export function canAccessBuiltinIntegrations($auth) {
  return $auth.hasAiAdvisorAccess;
}

/**
 * Users without full Kentik AI settings access may still manage runbooks via RBAC.
 */
export function isKentikAiRunbooksOnly($auth) {
  return !($auth.isAdministrator && $auth.hasAiAccess);
}

/**
 * Whether the user can open Kentik AI settings at all (full access, or runbooks via RBAC).
 */
export function canAccessKentikAiSettings($auth) {
  return (
    !isKentikAiRunbooksOnly($auth) ||
    $auth.hasRbacPermissions(TAB_READ_PERMISSIONS.runbooks) ||
    canAccessBuiltinIntegrations($auth)
  );
}

/**
 * Shared tab definitions for Kentik AI settings. Used by the settings page itself
 * and by the AI Advisor sidebar so both stay in sync.
 *
 * Functional AI tabs are visible only when the company has enabled Kentik AI
 * (canUseAiTools); admin status governs editability, not visibility (see
 * isKentikAiSettingsTabDisabled). Overview and Privacy & Security are informational
 * and always listed; Overview also holds the enable-Kentik-AI control, so it must stay
 * reachable before AI is turned on. Runbooks is RBAC-governed. AI Advisor Usage is only
 * meaningful when the Advisor itself is enabled (hasAiAdvisorAccess).
 */
export function getKentikAiSettingsTabConfigs($auth) {
  const { canUseAiTools, hasAiAdvisorAccess } = $auth;
  const hasAiModelsPermission = $auth.hasPermission('aiModels', { overrideForSudo: false });
  const hasDeviceConfigPermission = $auth.hasPermission('recon.deviceConfigFetch.enabled', {
    overrideForSudo: false
  });
  const hasAutoInvestigationPermission = $auth.hasPermission('aiInvestigation.enabled', {
    overrideForSudo: false
  });
  const hasMcpServersPermission = $auth.hasPermission('aiMcpServers.enabled', { overrideForSudo: false });

  return [
    { id: 'overview', title: 'Overview', icon: 'clean', condition: true },
    {
      id: 'models-and-credentials',
      title: 'Models & Credentials',
      icon: 'code-block',
      condition: canUseAiTools && hasAiModelsPermission
    },
    { id: 'context', title: 'Custom Network Context', icon: 'chat', condition: canUseAiTools },
    {
      id: 'nms-command-access',
      title: 'Command Access',
      icon: 'pulse',
      condition: canUseAiTools && hasDeviceConfigPermission
    },
    // always listed; access is governed purely by RBAC (see isKentikAiSettingsTabDisabled)
    { id: 'runbooks', title: 'Runbooks', icon: 'book', condition: true },
    {
      id: 'auto-investigation',
      title: 'Auto Investigation',
      icon: 'search',
      condition: canUseAiTools && hasAutoInvestigationPermission
    },
    {
      id: 'mcp-servers',
      title: 'MCP Servers',
      icon: 'data-connection',
      condition: canUseAiTools && hasMcpServersPermission
    },
    {
      id: INFOBLOX_IQ_TAB_ID,
      title: 'Infoblox',
      // Blueprint drops its tab-icon class (and spacing) from element icons, so it is added back here.
      icon: <InfobloxLogo size={20} className={Classes.TAB_ICON} />,
      tagContent: 'New',
      tagProps: { intent: 'success', minimal: true, round: true },
      condition: canAccessBuiltinIntegrations($auth)
    },
    { id: 'privacy-and-security', title: 'Privacy & Security', icon: 'manual', condition: true },
    { id: 'usage', title: 'AI Advisor Usage', icon: 'chart', condition: hasAiAdvisorAccess }
  ];
}

/**
 * Tabs in TAB_READ_PERMISSIONS are gated by their RBAC read permission.
 * PENDING_RBAC_TABS additionally fall back to isAdministrator until their migration runs.
 * Runbooks-only users can only access the runbooks tab.
 */
export function isKentikAiSettingsTabDisabled($auth, tabId) {
  if (tabId === INFOBLOX_IQ_TAB_ID) {
    return !canAccessBuiltinIntegrations($auth);
  }
  if (isKentikAiRunbooksOnly($auth)) {
    return tabId !== 'runbooks' || !$auth.hasRbacPermissions(TAB_READ_PERMISSIONS.runbooks);
  }
  const readPermissions = TAB_READ_PERMISSIONS[tabId];
  if (readPermissions) {
    return !$auth.hasRbacPermissions(readPermissions);
  }
  return false;
}

/**
 * Settings links the current user can actually open, e.g. for navigation menus outside the settings page.
 */
export function getKentikAiSettingsNavItems($auth, baseUrl = KENTIK_AI_SETTINGS_BASE_URL) {
  return getKentikAiSettingsTabConfigs($auth)
    .filter((tab) => tab.condition && !isKentikAiSettingsTabDisabled($auth, tab.id))
    .map(({ condition, ...tab }) => ({ ...tab, url: `${baseUrl}/${tab.id}` }));
}
